import React, {
    Suspense,
    memo,
    useMemo,
    useCallback,
    useEffect,
    useState,
} from 'react'
import { Switch, Route } from 'react-router-dom'
import { Box, useTheme, useMediaQuery } from '@mui/material'
import { History } from 'history'
import { match as Match } from 'react-router-dom'
import UpperMenu from '@/feature/navigation/Topmenu'
import MyPage from '../Pages/MyPage'
import { UpperMenuContact } from '@/feature/navigation/Topmenu'
import LoadingPage from '@/shared/composed-ui/LoadingPage'
import ErrorBoundary from '../Elements/ErrorBoundary'
import Reports from '../Admin/Pages/Reports/Reports'
import Favorites from './Favorites'
import { ChatProvider } from '../../context/ChatContext'
import AppNavigationDrawer, {
    APP_NAVIGATION_DRAWER_WIDTH,
} from './AppNavigationDrawer'

import * as LazyComponents from './lazyImports'

interface KedoEnv {
    getMenuStatus: () => boolean
    setMenuStatus: () => void
    clearMenuStatus: () => void
    getEnvironment: () => any
    getModule: (name: string) => { id?: string } | null
    hasCredential: (name: string) => boolean
    isEnvironmentAdmin: () => boolean
    clearInfo: () => void
}

interface KedoUtils {
    deleteSavedSearch: () => void
    deleteSavedLimit: () => void
}

interface Kedo {
    env: () => KedoEnv
    utils: () => KedoUtils
    isAdmin: () => boolean
}

interface Location {
    pathname: string
    search: string
    hash: string
    state?: any
}

interface DefaultPageProps {
    kedo: Kedo
    handleLogout: () => void
    history: History
    location: Location
    match: Match
}

interface MenuState {
    isOpen: boolean
}

const getInitialMenuState = (kedo: Kedo, isMobile: boolean): boolean => {
    return !isMobile ? kedo.env().getMenuStatus() : false
}

const DefaultPage: React.FC<DefaultPageProps> = ({
    kedo,
    handleLogout,
    history,
    location,
    match,
}) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const [menuOpen, setMenuOpen] = useState<boolean>(() =>
        getInitialMenuState(kedo, isMobile)
    )
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(false)
    const [navPresent, setNavPresent] = useState<boolean>(true)

    const isChatEnabled = useMemo<boolean>(() => {
        if (!kedo) return false

        return (
            kedo.isAdmin() ||
            (!!kedo?.env()?.getModule('artificialintelligence')?.id &&
                (!!kedo?.env()?.hasCredential('ai_chat') ||
                    kedo.env().isEnvironmentAdmin()))
        )
    }, [kedo])

    const hasEnvironment = !!kedo?.env()?.getEnvironment()

    const handleStateChange = useCallback(
        (isOpen: boolean): void => {
            setMenuOpen(isOpen)

            if (!isOpen) {
                localStorage.removeItem('menu')
                kedo.env().clearMenuStatus()
            } else {
                kedo.env().setMenuStatus()
            }
        },
        [kedo]
    )

    const closeMenu = useCallback((): void => {
        setMenuOpen(false)
    }, [])

    const handleSidebar = useCallback((state: MenuState): void => {
        setSidebarOpen(state.isOpen)
    }, [])

    const openSidebar = useCallback((): void => {
        setSidebarOpen(true)
    }, [])

    const closeSidebar = useCallback((): void => {
        setSidebarOpen(false)
    }, [])

    const moodPresentation = useCallback((statusPresent: boolean): void => {
        setMenuOpen(statusPresent)
        setNavPresent((prev) => !prev)
    }, [])

    useEffect(() => {
        if (isMobile && kedo.env().getMenuStatus()) {
            localStorage.removeItem('menu')
            kedo.env().clearMenuStatus()
        }
    }, [kedo, isMobile])

    useEffect(() => {
        const reset = /defdossier|contentdossier/.test(location.pathname)

        if (!reset) {
            kedo.utils().deleteSavedSearch()
            kedo.utils().deleteSavedLimit()
        }
    }, [location.pathname, kedo])

    // Close menu on route change (mobile only)
    useEffect(() => {
        if (isMobile && menuOpen) {
            setMenuOpen(false)
        }
    }, [location.pathname, isMobile])

    const mainContent = useMemo(() => {
        return (
            <Box
                component="section"
                id="main-content"
                data-cy="default-page-content"
            >
                {navPresent && (
                    <UpperMenu
                        kedo={kedo}
                        handleLogout={handleLogout}
                        handleSidebar={openSidebar}
                        history={history}
                        onMenuOpen={() => handleStateChange(true)}
                        menuOpen={menuOpen}
                        isMobile={isMobile}
                        drawerWidth={APP_NAVIGATION_DRAWER_WIDTH}
                    />
                )}
                <Box sx={{ p: 0 }}>
                    <Box
                        className="outerContainer mainContent"
                        data-cy="main-content"
                        sx={{
                            marginTop: navPresent ? undefined : '20px',
                        }}
                    >
                        <ErrorBoundary
                            kedo={kedo}
                            handleLogout={handleLogout}
                            history={history}
                            location={location}
                            match={match}
                        >
                            <Suspense fallback={<LoadingPage />}>
                                <Switch>
                                    <Route exact={true} path="/">
                                        <MyPage
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                            onCloseSidebar={moodPresentation}
                                        />
                                    </Route>
                                    <Route path={'/colleagues'}>
                                        <LazyComponents.Colleagues
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path={'/contact'}>
                                        <UpperMenuContact
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={'/contentdossier/create/:id'}>
                                        <LazyComponents.CreateContentDossier
                                            kedo={kedo}
                                            props={{
                                                kedo,
                                                handleLogout,
                                                history,
                                                location,
                                                match,
                                            }}
                                        />
                                    </Route>
                                    <Route exact path="/contextfilter/:id">
                                        <LazyComponents.ContextFilterDetail
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/contextfilter">
                                        <LazyComponents.ContextFilterIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={'/datastructure/:id'}>
                                        <LazyComponents.DataStructureEdit
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={'/datastructure'}>
                                        <LazyComponents.DataStructureIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={'/messagequeue'}>
                                        <LazyComponents.MessageQueueIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={'/contentdossier/:id'}>
                                        <LazyComponents.DetailContentDossier
                                            kedo={kedo}
                                            props={{
                                                kedo,
                                                handleLogout,
                                                history,
                                                location,
                                                match,
                                            }}
                                        />
                                    </Route>
                                    <Route path={'/subscriptions'}>
                                        <LazyComponents.MySubscriptions
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={'/events'}>
                                        <LazyComponents.EventsIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={'/defdossier/conditions/:id'}>
                                        <LazyComponents.DefDossierConditions
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={'/defdossier/edit/:id'}>
                                        <LazyComponents.DefDossierEdit
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={'/defdossier/:id'}>
                                        <LazyComponents.DefDossierIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path={'/profile'}>
                                        <LazyComponents.UserProfile
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                            me={true}
                                        />
                                    </Route>
                                    <Route exact path="/permissions">
                                        <LazyComponents.PermissionIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/security">
                                        <LazyComponents.SecurityIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/logs">
                                        <LazyComponents.LogIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/useractivity">
                                        <LazyComponents.UserActivityIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/modules">
                                        <LazyComponents.ModulesIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path={['/import/:id']}>
                                        <LazyComponents.ImportData
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/dossierflow/:id">
                                        <LazyComponents.DossierFlow
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/order/:id">
                                        <LazyComponents.MyOrder
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/blocks/:id">
                                        <LazyComponents.BlockDetail
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/blocks">
                                        <LazyComponents.BlocksIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/reports">
                                        <Reports
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/reports/overview">
                                        <LazyComponents.ReportsOverview
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/roles">
                                        <LazyComponents.RolesIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/role/:id">
                                        <LazyComponents.RoleDetail
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/user">
                                        <LazyComponents.UserIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/user/:id">
                                        <LazyComponents.UserProfile
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                            me={false}
                                        />
                                    </Route>
                                    <Route
                                        path={['/messages/:id', '/messages']}
                                    >
                                        <LazyComponents.MessageOverview
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={['/layout']}>
                                        <LazyComponents.Layout
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={['/files']}>
                                        <LazyComponents.Files
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route
                                        path={[
                                            '/templates/new',
                                            '/templates/:id',
                                        ]}
                                    >
                                        <LazyComponents.TemplateForm
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={['/templates']}>
                                        <LazyComponents.TemplateOverview
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path="/wordkedocx/:id">
                                        <LazyComponents.KedocxEditor
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={['/querybuilder/:id']}>
                                        <LazyComponents.QueryBuilderEditor
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={['/querybuilder']}>
                                        <LazyComponents.QueryBuilderIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={['/wordkedocx']}>
                                        <LazyComponents.KedocxIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route path={['/languages']}>
                                        <LazyComponents.Languages
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path={'/erd'}>
                                        <LazyComponents.ErdDiagram
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path={'/fieldeditor/:id'}>
                                        <LazyComponents.FieldEditor
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path={'/environment/edit'}>
                                        <LazyComponents.EnvironmentEdit
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path={'/upgrade'}>
                                        <LazyComponents.Upgrade
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route exact path={['/webhooks']}>
                                        <LazyComponents.WebhookIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                    <Route
                                        exact
                                        path={['/module/jwwmo/toewijzingen']}
                                    >
                                        <LazyComponents.ToewijzingIndex
                                            kedo={kedo}
                                            handleLogout={handleLogout}
                                            history={history}
                                            location={location}
                                            match={match}
                                        />
                                    </Route>
                                </Switch>
                            </Suspense>
                        </ErrorBoundary>
                    </Box>
                </Box>
            </Box>
        )
    }, [
        navPresent,
        kedo,
        handleLogout,
        openSidebar,
        menuOpen,
        isMobile,
        history,
        location,
        match,
        moodPresentation,
    ])

    if (!kedo) {
        return <LoadingPage />
    }

    // Previously: if (isLoggedIn && !hasEnvironment) return <Redirect to="/environment" />
    // Removed so idle relogin can keep the current route; ErrorBoundary recovery handles missing env.

    return (
        <ChatProvider isChatEnabled={isChatEnabled} kedo={kedo}>
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                {/* Print-hidden menu area */}
                <Box
                    data-cy="menu-drawer-area"
                    component="nav"
                    sx={{ display: { print: 'none' }, zIndex: 999 }}
                >
                    <AppNavigationDrawer
                        hasEnvironment={hasEnvironment}
                        navPresent={navPresent}
                        menuOpen={menuOpen}
                        isMobile={isMobile}
                        history={history}
                        kedo={kedo}
                        onMenuStateChange={handleStateChange}
                        closeMenu={closeMenu}
                    />
                </Box>

                {/* Main content area - auto-shifts with drawer */}
                <Box
                    component="main"
                    className="menu-col"
                    sx={{
                        flexGrow: 1,
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                        ...(!isMobile &&
                            menuOpen && {
                                transition: theme.transitions.create('width', {
                                    easing: theme.transitions.easing.easeOut,
                                    duration:
                                        theme.transitions.duration
                                            .enteringScreen,
                                }),
                            }),
                        width: !isMobile
                            ? menuOpen
                                ? `calc(100% - ${APP_NAVIGATION_DRAWER_WIDTH}px)`
                                : `100%`
                            : '100%',
                    }}
                >
                    {mainContent}
                </Box>

                {/* Print-hidden favorites drawer */}
                <Box
                    data-cy="favorites-drawer-area"
                    component="aside"
                    sx={{ display: { print: 'none' }, zIndex: 999 }}
                >
                    <Favorites
                        kedo={kedo}
                        handleSidebar={handleSidebar}
                        open={sidebarOpen}
                        closeSidebar={closeSidebar}
                    />
                </Box>
            </Box>
        </ChatProvider>
    )
}

export default memo(DefaultPage)
